import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as fsLimit,
  query,
  where,
} from "firebase/firestore";
import { db } from "src/firebase";
import {
  MeetingPolicyEvent,
  MeetingPolicyRole,
  UserMeetingPolicyStats,
  UserMeetingPolicyStatsByRole,
} from "../types/meeting-policy.entity";

const USER_MEETING_POLICY_STATS_COLLECTION = "user_meeting_policy_stats";
const MEETING_POLICY_EVENTS_COLLECTION = "meeting_policy_events";
const MEETING_POLICY_ROLES: MeetingPolicyRole[] = ["LEARNER", "COACH"];

class MeetingPolicyRepository {
  async getStatsByUserId(userId: string): Promise<UserMeetingPolicyStatsByRole> {
    const entries = await Promise.all(
      MEETING_POLICY_ROLES.map(async (role) => {
        const statsDoc = await getDoc(doc(db, USER_MEETING_POLICY_STATS_COLLECTION, `${userId}_${role}`));

        if (!statsDoc.exists()) {
          return [role, null] as const;
        }

        return [
          role,
          {
            id: statsDoc.id,
            ...statsDoc.data(),
          } as UserMeetingPolicyStats,
        ] as const;
      })
    );

    return entries.reduce<UserMeetingPolicyStatsByRole>(
      (statsByRole, [role, stats]) => ({
        ...statsByRole,
        [role]: stats,
      }),
      { LEARNER: null, COACH: null }
    );
  }

  async getEventsByUserId(userId: string, eventsLimit = 75): Promise<MeetingPolicyEvent[]> {
    const eventsRef = collection(db, MEETING_POLICY_EVENTS_COLLECTION);
    const safeLimit = Math.min(Math.max(eventsLimit, 1), 100);
    const eventsQuery = query(eventsRef, where("userId", "==", userId), fsLimit(safeLimit));
    const snapshot = await getDocs(eventsQuery);
    const events = snapshot.docs.map(
      (eventDoc) =>
        ({
          id: eventDoc.id,
          ...eventDoc.data(),
        }) as MeetingPolicyEvent
    );

    return events
      .sort((a, b) => getTimestampMillis(b.occurredAt || b.createdAt) - getTimestampMillis(a.occurredAt || a.createdAt))
      .slice(0, safeLimit);
  }
}

const getTimestampMillis = (value: unknown) => {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "object" && value !== null && "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate().getTime();
  }
  if (typeof value === "object" && value !== null && "seconds" in value) {
    return Number((value as { seconds: number }).seconds) * 1000;
  }

  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const meetingPolicyRepository = new MeetingPolicyRepository();
